import React, { FC, useMemo, useState, useRef, useEffect } from 'react';

import { useFormattedUser } from '@adsk/alloy-provider';
import { warn } from '@adsk/alloy-react-helpers';
import { useOnlyLocale } from '@adsk/alloy-react-locale';
import { MenuItemType } from '@adsk/alloy-react-menu';

import AttachmentFileHandlerItem from './AttachmentFileHandlerItem/AttachmentFileHandlerItem';
import AttachmentTileItem from './AttachmentTileItem/AttachmentTileItem';
import { hasLocalThumbnail, hasLocalView } from '../../api/attachments';
import { ATTACHMENTS_LIST_VARIANTS } from '../../consts';
import { useAttachmentsContext } from '../../context/AttachmentsContext';
import { useTmpLocalViews } from '../../context/LocalViewsProvider';
import formatCreatedOn from '../../helpers/formatCreatedOn';
import formatFileSize from '../../helpers/formatFileSize';
import useThumbnail from '../../hooks/useThumbnail';
import { useTranslation } from '../../i18n';
import { AttachmentListVariant } from '../../types';
import AttachmentDeleteConfirmationModal from '../common/AttachmentDeleteConfirmationModal';

export type AttachmentItemProps = {
  draft?: boolean;
  id: string;
  name: string;
  createdOn?: string;
  createdBy?: string;
  /**
   * @deprecated
   * This prop is deprecated and will be removed in the future. Use createdBy instead.
   */
  createdByName?: string;
  allowDelete?: boolean;
  allowDownload?: boolean;
  onDelete: (attachmentId: string) => void;
  uploadError?: string;
  variant?: AttachmentListVariant;
  size: number;
  onClick?: () => void;
  onDownload?: () => void;
  onRetry?: () => void;
  showCreatedOn?: boolean;
  showCreatedBy?: boolean;
  showSize?: boolean;
  uploaded: boolean;
  rejected?: boolean;
  customItemActions?: MenuItemType<string>[];
  isDeleted?: boolean;
  condensed?: boolean;
};

const AttachmentItem: FC<AttachmentItemProps> = ({
  id,
  name,
  createdOn,
  createdBy,
  createdByName,
  allowDelete = true,
  allowDownload = true,
  onDelete,
  uploadError,
  draft,
  variant = ATTACHMENTS_LIST_VARIANTS.GRID,
  size,
  onClick,
  onDownload,
  showCreatedOn = true,
  showCreatedBy = true,
  showSize = true,
  onRetry,
  uploaded,
  rejected,
  customItemActions,
  isDeleted,
  condensed,
}) => {
  const { t } = useTranslation();
  const { locale } = useOnlyLocale();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string>();
  const showCreatedByWarn = useRef<boolean>(false);
  const [isContextualMenuOpen, setIsContextualMenuOpen] =
    useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const { thumbnail } = useThumbnail({
    attachmentId: id,
  });

  const { formatDisplayUser } = useFormattedUser();

  const { uploadIndicationIds } = useAttachmentsContext();
  const { localViews } = useTmpLocalViews();

  const showUploadSuccessIndication = useMemo(
    () => uploadIndicationIds.includes(id),
    [id, uploadIndicationIds],
  );

  const authorName = useMemo(
    () =>
      createdBy ? formatDisplayUser(createdBy).displayName : createdByName,
    [createdBy, createdByName, formatDisplayUser],
  );

  const metadata = useMemo(() => {
    const metadataArray = [];

    if (showCreatedOn && createdOn) {
      metadataArray.push(formatCreatedOn(createdOn, locale));
    }

    if (showCreatedBy && authorName) {
      metadataArray.push(authorName);
    }

    if (showSize && size) {
      metadataArray.push(formatFileSize(size));
    }

    return metadataArray.join(', ');
  }, [
    showCreatedOn,
    createdOn,
    locale,
    showCreatedBy,
    authorName,
    showSize,
    size,
  ]);

  const mapVariantToItemComponent = {
    [ATTACHMENTS_LIST_VARIANTS.LIST]: AttachmentFileHandlerItem,
    [ATTACHMENTS_LIST_VARIANTS.GRID]: AttachmentTileItem,
  };

  const ItemComponent = mapVariantToItemComponent[variant];

  const onDeleteWrapper = async () => {
    setIsDeleting(true);
    try {
      await onDelete(id);
    } catch {
      setDeleteError(t('DELETE_FAILED') as string);
      setIsDeleting(false);
    }
  };

  const isUploading = draft && !uploaded;

  useEffect(() => {
    if (!showCreatedByWarn.current && !createdBy && createdByName) {
      warn(
        'AttachmentItem - passing createdByName without createdBy is deprecated, please make sure to pass createdBy as well',
      );
      showCreatedByWarn.current = true;
    }
  }, [createdBy, createdByName]);

  return (
    <>
      <ItemComponent
        name={name}
        metadata={metadata}
        onDelete={() => {
          if (draft) {
            onDeleteWrapper();
          } else {
            setIsConfirmationModalOpen(true);
          }
        }}
        isDeleting={isDeleting}
        error={uploadError || deleteError}
        allowDelete={allowDelete}
        isContextualMenuOpen={isContextualMenuOpen}
        setIsContextualMenuOpen={setIsContextualMenuOpen}
        thumbnail={
          thumbnail
            ? thumbnail
            : hasLocalThumbnail(name) && localViews[id]
              ? localViews[id]
              : undefined
        }
        onClick={onClick}
        onRetry={onRetry}
        allowDownload={allowDownload && !draft}
        onDownload={onDownload}
        allowRetry={!rejected}
        isUploading={isUploading}
        customItemActions={customItemActions}
        isDeleted={isDeleted}
        condensed={condensed}
        showUploadSuccessIndication={showUploadSuccessIndication}
        style={
          (draft && hasLocalView(name)) || !draft ? { cursor: 'pointer' } : {}
        }
      />
      <AttachmentDeleteConfirmationModal
        open={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={() => {
          onDeleteWrapper();

          setIsConfirmationModalOpen(false);
        }}
        fileName={name}
      />
    </>
  );
};

export default AttachmentItem;
